import { useAxiosConfig } from '@shared/api/AxiosConfigProvider';
import { refreshQueries } from '@shared/api/hooks/refresh-queries';
import {
  getSoldGoodsFoodListQueryKey,
  getSoldGoodsFoodRecordsListQueryKey,
  getSoldGoodsFoodRetrieveQueryKey,
  useSoldGoodsDestroy,
  useSoldGoodsFoodCoverageRetrieve,
  useSoldGoodsFoodList,
  useSoldGoodsFoodPartialUpdate,
  useSoldGoodsFoodPreviewRetrieve,
  useSoldGoodsFoodRecordsList,
  useSoldGoodsFoodRecordsPartialUpdate,
  useSoldGoodsFoodRecordsUpdate,
  useSoldGoodsFoodRetrieve,
  useSoldGoodsFoodUpdate,
  useSoldGoodsRecordsDestroy,
} from '@shared/api/lib/sold-goods/sold-goods';

import {
  ActivityCoverage,
  FoodActivityRecordUpdate,
  FoodActivityRetrieveUpdate,
  FoodOperationActivityDetail,
  PaginatedFoodActivitiesListList,
  PaginatedFoodOperationActivityRecordList,
  SoldGoodsFoodListParams,
  SoldGoodsFoodPreviewRetrieveParams,
  SoldGoodsFoodRecordsListParams,
} from '@shared/api/types';
import { useQueryClient } from '@tanstack/react-query';

export const useGetSoldFoodAndDrinkList = (
  params?: SoldGoodsFoodListParams
) => {
  return useSoldGoodsFoodList<PaginatedFoodActivitiesListList>(params, {
    request: useAxiosConfig(),
    query: {
      keepPreviousData: true,
    },
  });
};

export const useGetSoldFoodAndDrinkItem = ({
  activityUuid,
}: {
  activityUuid: string;
}) => {
  return useSoldGoodsFoodRetrieve<FoodOperationActivityDetail>(activityUuid, {
    request: useAxiosConfig(),
  });
};

export const useUpdateSoldFoodAndDrinkItem = () => {
  return useSoldGoodsFoodUpdate<FoodActivityRetrieveUpdate>({
    request: useAxiosConfig(),
  });
};

export const usePatchSoldFoodAndDrinkItem = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsFoodPartialUpdate<FoodActivityRetrieveUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: (_, variables) =>
        Promise.all([
          refreshQueries(
            queryClient,
            getSoldGoodsFoodRetrieveQueryKey(variables.activityUuid)
          ),
          refreshQueries(queryClient, getSoldGoodsFoodListQueryKey()),
        ]),
    },
  });
};

export const useGetSoldFoodAndDrinkPreview = ({
  params,
  enabled,
}: {
  params?: SoldGoodsFoodPreviewRetrieveParams;
  enabled?: boolean;
}) => {
  return useSoldGoodsFoodPreviewRetrieve(params, {
    request: useAxiosConfig(),
    query: {
      enabled: enabled,
      cacheTime: 0,
      staleTime: 0,
    },
  });
};

export const useDeleteSoldFoodAndDrinkItem = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: () =>
        refreshQueries(queryClient, getSoldGoodsFoodListQueryKey()),
    },
  });
};

export const useGetSoldFoodAndDrinkItemRecords = ({
  activityUuid,
  params,
}: {
  activityUuid: string;
  params?: SoldGoodsFoodRecordsListParams;
}) => {
  return useSoldGoodsFoodRecordsList<PaginatedFoodOperationActivityRecordList>(
    activityUuid,
    params,
    {
      request: useAxiosConfig(),
      query: {
        keepPreviousData: true,
      },
    }
  );
};

export const useUpdateSoldFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsFoodRecordsUpdate<FoodActivityRecordUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getSoldGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const usePatchSoldFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsFoodRecordsPartialUpdate<FoodActivityRecordUpdate>({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getSoldGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};
1;

export const useDeleteSoldFoodAndDrinkItemRecord = () => {
  const queryClient = useQueryClient();
  return useSoldGoodsRecordsDestroy({
    request: useAxiosConfig(),
    mutation: {
      onSuccess: async (_, variables) => {
        await queryClient.refetchQueries(
          getSoldGoodsFoodRecordsListQueryKey(variables.activityUuid)
        );
      },
    },
  });
};

export const useGetSoldFoodAndDrinkCoverage = () => {
  return useSoldGoodsFoodCoverageRetrieve<ActivityCoverage>({
    request: useAxiosConfig(),
  });
};

/**
 * Bulk actions on sold food (model-as-basic-food / model-as-recipe) now go
 * through the unified `useBulkUpdateSoldGoods` hook in `./bulk` — the action
 * travels inside the payload.
 */
